import { useState, useEffect } from "react";
import to from "await-to-js";
import { DialogContent, DialogTitle, TextField } from "@mui/material";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import cn from "clsx";

import UKDialog from "@/common-components-src/components/UKDialog/index";
import IMAGEAPI_TYPE from "@/common-components-src/const/imageApiType";
import UKUpload from "@/common-components-src/components/UKUpload";
import UKToast from "@/common-components-src/components/UKToast/index";
import UKLoadingIcon from "@/common-components-src/components/UKLoadingIcon";
import { useStores } from "@/app/csr-src/models";

import RuleWrap from "../ruleWrap";
import CommonButton from "../commonButton";
import CommonSelect from "../commonSelect";
import AddressSelect from "../addressSelect";
import AddressAdd from "../addressAdd";
import EmptyPage from "../emptyPage";
// import CardAmount from "../cardAmount";
import LocationModal from "../locationModal";
import styles from "./index.module.scss";

import {
  fetchRecordApplyInfo,
  fetchAddressList,
  fetchPostHouseList,
  fetchApplyReasonList,
  recordApplyCreate,
} from "@/app/csr-src/apis/afterSales/afterSales.api";
import {
  AddressVo,
  ApplyReasonResVo,
  IRecordApplyInfoReq,
  RecordApplyInfoResVo,
  PostHouseListResVo,
  ReturnCustomerInfoVo,
} from "@/app/csr-src/apis/afterSales/afterSales.interface";

// 图标
import Expand from "@/app/csr-src/assets/icons/chevron-down.svg";
import LocationIco from "@/app/csr-src/assets/icons/LocationIco.svg";

/* eslint-disable @typescript-eslint/no-explicit-any */
interface refundModalProps {
  orderId: string;
  isOpen: boolean;
  closeHandler: () => void;
  handleConfirmClick: (val: any) => void;
  postCode: string;
  skuIds?: string;
}

const ReturnRequestModal: React.FC<refundModalProps> = observer(({ orderId, isOpen, closeHandler, handleConfirmClick, postCode, skuIds }) => {
  const { t } = useTranslation();
  const { AfterSalesStore } = useStores();

  // apply info
  const [applyInfo, setApplyInfo] = useState<RecordApplyInfoResVo | null>(null);

  // Pick-up address
  const [addressList, setAddressList] = useState<AddressVo[] | []>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false); // 提交接口loading

  // reason
  const [resonList, setResonList] = useState<ApplyReasonResVo[] | []>([]);

  const [locationList, setLocationList] = useState<PostHouseListResVo[] | []>([]);

  const [curReason, setCurReason] = useState("");
  // 默认选中Drop-off
  const [curMethod, setCurMethod] = useState("");
  const [curLocation, setCurLocation] = useState("");
  const [curLocationInfo, setCurLocationInfo] = useState<PostHouseListResVo | null>(null);
  const [curAddress, setCurAddress] = useState("");
  const [defaultAddress, setDefaultAddress] = useState<ReturnCustomerInfoVo | null>(null);
  const [isDefault, setIsDefault] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);

  const [inputValue, setInputValue] = useState("");
  const maxCharacters = 500; // 设置最大字符数

  const [imageList, setImageList] = useState<string[] | undefined>();
  const [videoList, setVideoList] = useState<string[] | null>();

  // 查询地址列表
  const loadAddresslList = async () => {
    const [err, addressListData] = await to(fetchAddressList());

    if (err) {
      // setError("Failed to fetch address list");
      setAddressList([]);
    } else {
      setAddressList(addressListData as AddressVo[]);
      // setError(null);
    }
  };

  // 查询支持的售后原因列表
  const loadResonlList = async () => {
    const [err, reasonListData] = await to(
      fetchApplyReasonList({
        reasonApplyType: 1,
      }),
    );

    if (err) {
      // setError("Failed to fetch address list");
      setResonList([]);
    } else {
      const formatReasonListData = reasonListData?.map((item) => {
        return { ...item, reasonId: String(item.reasonId) };
      });
      setResonList(formatReasonListData as ApplyReasonResVo[]);
      // setError(null);
    }
  };

  // 获取驿站信息列表
  const loadPostHouseList = async () => {
    const [err, postHouseListData] = await to(
      fetchPostHouseList({
        postCode,
      }),
    );

    if (err) {
      // setError("Failed to fetch address list");
      setLocationList([]);
    } else {
      setLocationList(postHouseListData as PostHouseListResVo[]);
      postHouseListData?.[0]?.postHouseId && setCurLocation(postHouseListData?.[0]?.postHouseId);
      // setError(null);
    }
  };

  // 查询进入售后单申请详情页，订单以及退款相关信息
  const loadApplyInfo = async () => {
    const requestData: IRecordApplyInfoReq = {
      orderId: String(orderId),
      skuInfoList: AfterSalesStore?.selectedProductList,
    };
    const [err, applyInfoData] = await to(fetchRecordApplyInfo(requestData));

    if (err) {
      setError("Failed to fetch order details");
    } else {
      console.log(applyInfoData, "sjkk");
      setApplyInfo(applyInfoData);
      // Pick-up Address 默认选中订单的收货地址
      if (applyInfoData?.returnCustomerInfo?.addressInfo?.addressId) {
        setCurAddress(String(applyInfoData?.returnCustomerInfo?.addressInfo?.addressId));
      }
      setDefaultAddress(applyInfoData?.returnCustomerInfo);
      if (applyInfoData?.returnModelList?.length) {
        setCurMethod("70");
      }
      setError(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPostHouseList();
    loadApplyInfo();
    loadAddresslList();
    loadResonlList();
  }, []);

  const selectChangeHandler = (val: string) => {
    setCurReason(val);
  };

  const methodChangeHandler = (val: string) => {
    setCurMethod(val);
    // todo 切换后清空重置select的值
  };

  const locationChangeHandler = (val: any) => {
    setShowLocationModal(false);
    setCurLocation(val.postHouseId);
    setCurLocationInfo(val);
  };

  const addressChangeHandler = (val: string) => {
    setIsDefault(false);
    setCurAddress(val);
  };

  // 保存地址
  const saveCloseHandler = () => {
    loadAddresslList();
    setShowAddModal(false);
  };

  // 输入框输入
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length <= maxCharacters) {
      // 限制输入字符数
      setInputValue(value);
    }
  };

  // 图片上传
  const onUploadMedia = (mediaList: Record<string, string[]> | null) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (mediaList?.length) {
      setImageList(mediaList as any);
    }
    // setCurrentSkuMedia({ ...(currentSkuMedia || {}), [skuId]: mediaList } as any);
  };

  // const isBtnEnable = () => {
  //   let enable = false;
  //   if (curReason && curMethod && inputValue?.length >= 40) {
  //     if ((curMethod === "70" && curLocation) || (curMethod === "4" && curAddress)) {
  //       enable = true;
  //     }
  //   }
  //   return enable;
  // };

  // 创建售后服务单
  const createRecord = async () => {
    // 增加校验
    if (!curReason) {
      UKToast.show({
        icon: "warning",
        content: "Please select a reason first",
      });
      return;
    }
    // // 描述必须大于40个字符
    // if (!(inputValue?.length >= 40)) {
    //   UKToast.show({
    //     icon: "warning",
    //     content: "Please provide a detailed reason of at least 40 characters",
    //   });
    //   return;
    // }
    if (!curMethod) {
      UKToast.show({
        icon: "warning",
        content: "Please select return method",
      });
      return;
    }
    // 暂时注释前端校验
    // if (curMethod === "70" && !curLocation) {
    //   UKToast.show({
    //     icon: "warning",
    //     content: "Please select a Drop-off Location",
    //   });
    //   return;
    // }
    if (curMethod === "4" && !curAddress) {
      UKToast.show({
        icon: "warning",
        content: "Please select a Pick-up Address",
      });
      return;
    }
    setSubmitLoading(true);
    const customerExpect = applyInfo?.skuList?.[0]?.serviceTypeList?.[0]?.serviceType;
    const wareList =
      AfterSalesStore?.selectedProductList?.map(
        ({
          skuId,
          skuUUid,
          skuName,
          productType,
          applyCount,
          venderId,
        }: {
          skuId: string;
          skuUUid: string;
          skuName: string;
          productType: number;
          applyCount: number;
          venderId: string;
        }) => ({
          skuId,
          skuUUid,
          skuName,
          productType,
          applyCount,
          venderId,
        }),
      ) || [];
    const refundType = applyInfo?.refundTypeList?.[0]?.type;
    const selectedAddressInfo = addressList.find((ele) => String(ele.addressId) === curAddress);

    const { contactName, contactTel, contactTelCountry, addressInfo } = applyInfo?.returnCustomerInfo || {};
    let pickupParams = {};
    if (curMethod === "70") {
      // 选择某个驿站
      if (curLocationInfo) {
        pickupParams = {
          pickwareAddrFirstId: curLocationInfo?.provinceId,
          pickwareAddrSecondId: curLocationInfo?.cityId,
          pickwareAddrThirdId: curLocationInfo?.countyId,
          pickwareAddrFourthId: curLocationInfo?.townId,
          pickupAddrId: -1, // 暂定-1
          pickwareAddr: curLocationInfo?.address,
          pickupLongitude: curLocationInfo?.longitude,
          pickupLatitude: curLocationInfo?.latitude,
          pickupContactName: curLocationInfo?.postHouseName,
          pickupContactTel: curLocationInfo?.phone,
          postcode: curLocationInfo?.postCode,
          stationName: curLocationInfo?.postHouseName,
          postHouseId: curLocationInfo?.postHouseId,
        };
      } else {
        // 展示默认驿站
        pickupParams = {
          pickwareAddrFirstId: locationList[0]?.provinceId,
          pickwareAddrSecondId: locationList[0]?.cityId,
          pickwareAddrThirdId: locationList[0]?.countyId,
          pickwareAddrFourthId: locationList[0]?.townId,
          pickupAddrId: -1, // 暂定-1
          pickwareAddr: locationList[0]?.address,
          pickupLongitude: locationList[0]?.longitude,
          pickupLatitude: locationList[0]?.latitude,
          pickupContactName: locationList[0]?.postHouseName,
          pickupContactTel: locationList[0]?.phone,
          postcode: locationList[0]?.postCode,
          stationName: locationList[0]?.postHouseName,
          postHouseId: locationList[0]?.postHouseId,
        };
      }
    } else {
      // 选择上门取件地址
      if (selectedAddressInfo && !isDefault) {
        pickupParams = {
          pickwareAddrFirstId: selectedAddressInfo?.provinceId,
          pickwareAddrSecondId: selectedAddressInfo?.cityId,
          pickwareAddrThirdId: selectedAddressInfo?.countyId,
          pickwareAddrFourthId: selectedAddressInfo?.townId,
          pickupAddrId: curAddress,
          pickwareAddr: selectedAddressInfo?.fullAddress,
          pickupLongitude: selectedAddressInfo?.longitude,
          pickupLatitude: selectedAddressInfo?.latitude,
          pickupContactName: selectedAddressInfo?.name,
          pickupContactTel: selectedAddressInfo?.mobile,
        };
      } else {
        // 默认展示的地址
        pickupParams = {
          pickwareAddrFirstId: addressInfo?.provinceId,
          pickwareAddrSecondId: addressInfo?.cityId,
          pickwareAddrThirdId: addressInfo?.countryId,
          pickwareAddrFourthId: addressInfo?.townId,
          pickupAddrId: curAddress,
          pickwareAddr: addressInfo?.address,
          pickupLongitude: addressInfo?.longitude,
          pickupLatitude: addressInfo?.latitude,
          pickupContactName: contactName,
          pickupContactTel: contactTel,
        };
      }
    }
    recordApplyCreate({
      orderId,
      afsSkuQueries: wareList,
      customerExpect,
      returnModel: curMethod,
      refundType,
      questionDesc: inputValue,
      reasonId: curReason,
      ...pickupParams,
      images: imageList,
      videoIds: videoList as any,
      contactTelCountry,
      contactName,
      contactTel,
      returnAddr: addressInfo?.address,
      returnAddrFirstId: addressInfo?.provinceId,
      returnAddrSecondId: addressInfo?.cityId,
      returnAddrThirdId: addressInfo?.countryId,
      returnAddrFourthId: addressInfo?.townId,
      returnAddrId: addressInfo?.addressId,
    })
      .then((recordApplyCreateData) => {
        if (recordApplyCreateData) {
          handleConfirmClick && handleConfirmClick(recordApplyCreateData);
        } else {
          UKToast.show({
            icon: "warning",
            content: "Submit failed. Please try again.",
          });
        }
      })
      .catch(() => {
        UKToast.show({
          icon: "error",
          content: "Oops! Something went wrong. Please try again.",
        });
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const formatReturnModelListData = (
    list:
      | {
          text: string;
          type: number;
        }[]
      | [],
  ) => {
    return list.map((item) => {
      return {
        reasonId: String(item.type),
        reasonName: item.text,
      };
    });
  };

  const methodList = [
    {
      title: "Self drop-off",
      desc: "Items can be delivered to any drop-off point",
      type: "70",
    },
    // {
    //   title: "Courier pick-up",
    //   desc: "Book a slot and wait for courier to pick-up",
    //   type: "4",
    // },
  ];

  return (
    <>
      <UKDialog open={isOpen} onClose={closeHandler} showCloseBtn={true} className={styles.refund_modal}>
        <DialogTitle>{t("order.orderDetail.returnRequestModal.modalTitle")}</DialogTitle>
        <DialogContent>
          {loading ? (
            <div className={styles.loading_wrap}>
              <UKLoadingIcon type="black" />
              <span className={styles.loading_text}>{t("common.pageStatus.loading")}</span>
            </div>
          ) : applyInfo?.returnModelList ? (
            <>
              <div className={styles.ruleContainer}>
                <RuleWrap />
              </div>
              <div className={styles.refund_wrap}>
                {/* reason */}
                <div className={styles.refund_item_title}>
                  <div className={styles.refund_item_title_left}>
                    <span className={styles.require_style}>*</span>
                    {t("order.orderDetail.refundModal.reasonText")}
                  </div>
                </div>
                <div className={styles.reason_wrap}>
                  <CommonSelect
                    selectValue={curReason}
                    selectList={resonList || []}
                    selectChangeHandler={selectChangeHandler}
                    placeholder={t("order.orderDetail.returnRequestModal.reasonPlaceholder")}
                  />
                </div>
                {/* detailed description */}
                <div className={styles.refund_item_title}>
                  <div className={styles.refund_item_title_left}>
                    {/* <span className={styles.require_style}>*</span> */}
                    {t("order.orderDetail.returnRequestModal.descriptionText")}
                  </div>
                </div>
                <div className={styles.upload_wrap}>
                  {/* 上传图片 */}
                  {/* <UKUpload
                    moduleType="REVIEW"
                    onChange={(media: any) => onUploadMedia(media)}
                    limitNumber={5}
                  /> */}
                  <UKUpload
                    videoBussinessType="refund"
                    videoParam={{
                      skuIds,
                      orderId,
                    }}
                    type="image_video"
                    limitNumber={videoList?.length ? 4 : 5}
                    limitVideoNumber={(imageList?.length as any) > 4 ? 0 : 1}
                    apiType={IMAGEAPI_TYPE.AfterSale}
                    onChange={(media: any) => onUploadMedia(media)}
                    onVideoChange={(media: any) => {
                      const videos = media?.map((item: any) => String(item.vid));
                      if (videos?.length) {
                        setVideoList(videos);
                      } else {
                        setVideoList(null);
                      }
                    }}
                  />
                  <div className={styles.uploadTips}>Add up to 5</div>
                </div>
                <div className={styles.detail_wrap}>
                  <TextField
                    id="outlined-multiline-flexible"
                    multiline
                    maxRows={4}
                    minRows={4}
                    value={inputValue}
                    onChange={handleInputChange}
                    fullWidth
                    placeholder={t("order.orderDetail.returnRequestModal.detailPlaceholder")}
                    className="detail-textfield"
                    style={{
                      lineHeight: "20px",
                      color: "#1A1A1A",
                      fontSize: "16px",
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        background: "#f7f8fc",
                        paddingTop: "10px",
                        paddingBottom: "15px",
                      },
                      "& fieldset": {
                        border: "none",
                      },
                    }}
                  />
                  <div className={styles.input_tip}>{`${inputValue.length}/${maxCharacters}`}</div>
                </div>

                {/* return method */}
                <div className={styles.refund_item_title}>
                  <div className={styles.refund_item_title_left}>
                    <span className={styles.require_style}>*</span>
                    {t("order.orderDetail.returnRequestModal.returnMethodText")}
                  </div>
                </div>
                <div className={cn(styles.reason_wrap, styles.methodWrap)}>
                  {/* <CommonSelect
                    selectValue={curMethod}
                    selectList={formatReturnModelListData(applyInfo?.returnModelList || [])}
                    selectChangeHandler={methodChangeHandler}
                  /> */}
                  {methodList.map((item, index) => {
                    return (
                      <div
                        className={cn(styles.methodItem, { [styles.methodActiveItem]: item.type === curMethod })}
                        key={index}
                        onClick={() => {
                          setCurMethod(item.type);
                        }}
                      >
                        <div className={styles.methodItemTitle}>{item.title}</div>
                        <div className={styles.methodItemDesc}>{item.desc}</div>
                      </div>
                    );
                  })}
                </div>
                {/* Drop-off location */}
                {curMethod === "70" ? (
                  <div
                    className={styles.dropOffWrap}
                    onClick={() => {
                      setShowLocationModal(true);
                    }}
                  >
                    <div className={styles.dropOffTitle}>
                      <div>Drop-off points</div>
                      <div className={styles.entryWrap}>
                        See all available drop-off points
                        <Expand className={styles.entryIco} />
                      </div>
                    </div>
                    {curLocationInfo ? ( // 选择后的驿站
                      <div className={styles.dropOffContent}>
                        <div className={styles.dropOffContentMain}>
                          <LocationIco className={styles.locationIcoStyle} />
                          <span className={styles.locationName}>{curLocationInfo.postHouseName}</span>
                        </div>
                        {/* <div>{curLocationInfo.distance}</div> */}
                      </div>
                    ) : locationList[0]?.postHouseName ? ( // 初始化推荐的驿站
                      <div className={styles.dropOffContent}>
                        <div className={styles.dropOffContentMain}>
                          <span className={styles.recommendStyle}>Recommended point</span>
                          <LocationIco className={styles.locationIcoStyle} />
                          <span className={styles.locationName}>{locationList[0]?.postHouseName}</span>
                        </div>
                        {/* <div>{locationList[0]?.distance}</div> */}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {/* Pick-up address */}
                {curMethod === "4" ? (
                  <>
                    <div className={styles.refund_item_title}>
                      <div className={styles.refund_item_title_left}>
                        <span className={styles.require_style}>*</span>
                        {t("order.orderDetail.returnRequestModal.pickUpText")}
                      </div>
                    </div>
                    <div className={styles.reason_wrap}>
                      <AddressSelect
                        selectValue={curAddress}
                        selectList={addressList || []}
                        selectChangeHandler={addressChangeHandler}
                        placeholder={t("order.orderDetail.returnRequestModal.addressPlaceholder")}
                        addAddressClick={() => setShowAddModal(true)}
                        defaultAddress={defaultAddress}
                        useDefault={isDefault}
                      />
                    </div>
                  </>
                ) : null}
                {/* Estimated refund amount and method */}
                {/* <div className={styles.content_wrap}>
                <div className={styles.refund_item_title}>
                  <div className={styles.refund_item_title_left}>
                    Estimated refund amount and method
                  </div>
                  <TipIcon className={styles.tip_ico} />
                </div>
                <CardAmount
                  amountInfo={{
                    cardNum: "8809 **** ****1290",
                    price: "7.33",
                    unit: "£",
                  }}
                />
                <div className={styles.refund_item_sub_title}>
                  <div className={styles.refund_item_title_left}>
                    {1} coupon(s) would be returned
                  </div>
                  <TipIcon className={styles.tip_ico} />
                </div>
                <div className={styles.coupon_desc}>
                  1 coupon(s) for specific items will be returned as it meets the return rules.
                </div>
              </div> */}
                <div className={styles.oper_wrap}>
                  <CommonButton
                    variant="outlined"
                    onClick={closeHandler}
                    style={{
                      marginRight: "12px",
                    }}
                  >
                    {t("order.orderList.common.cancelBtnText")}
                  </CommonButton>
                  <CommonButton variant="contained" onClick={createRecord} disabled={submitLoading}>
                    {t("order.orderList.common.submitBtnText")}
                  </CommonButton>
                </div>
              </div>
            </>
          ) : (
            <EmptyPage />
          )}
        </DialogContent>
      </UKDialog>
      {showAddModal && (
        <AddressAdd
          closeHandler={() => {
            setShowAddModal(false);
          }}
          newCreateSaveClose={saveCloseHandler}
        />
      )}
      {showLocationModal && (
        <LocationModal
          isOpen={showLocationModal}
          selectValue={curLocation}
          selectChangeHandler={locationChangeHandler}
          initPostCode={postCode}
          closeHandler={() => {
            setShowLocationModal(false);
          }}
          selectPostCode={curLocationInfo?.postCode || ""}
        />
      )}
    </>
  );
});

export default ReturnRequestModal;
